import { FC } from 'react'
import styled, { keyframes, Keyframes } from 'styled-components'
import { compose, color, ColorProps, space, SpaceProps } from 'styled-system'

// Una torta que se vacia en `durationMs` para hacer visible una espera que de
// otro modo no se ve en ninguna parte (el debounce de un filtro, por ejemplo).
//
// La cuenta corre en CSS, no en React: no hay timers ni un render por cuadro.
// Como contrapartida, no se reinicia sola al cambiar `durationMs`; para volver
// a empezar hay que remontar el componente con un `key` que cambie junto con
// aquello que reinicia la espera.

const drain = (circumference: number): Keyframes => keyframes`
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: ${circumference};
  }
`

interface PieProps extends ColorProps, SpaceProps {
  durationMs: number
  circumference: number
}

const Pie = styled.svg<PieProps>`
  ${compose(color, space)}
  flex-shrink: 0;
  /* el trazo parte a las 12 y no a las 3 */
  transform: rotate(-90deg);
  & .wedge {
    animation: ${({ circumference }) => drain(circumference)}
      ${({ durationMs }) => durationMs}ms linear forwards;
  }
`

interface ICountdownPieProps extends SpaceProps {
  durationMs: number
  size?: number
  color?: string
}

const CountdownPie: FC<ICountdownPieProps> = ({
  durationMs,
  size = 14,
  color = `primary.2`,
  ...props
}) => {
  const center = size / 2
  // Un circulo de radio r/2 con un trazo de grosor r se ve como una torta
  // llena, asi que el dash del trazo recorta una porcion y no un anillo.
  const radius = size / 4
  const circumference = 2 * Math.PI * radius

  return (
    <Pie
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      color={color}
      durationMs={durationMs}
      circumference={circumference}
      aria-hidden="true"
      {...props}
    >
      <circle
        cx={center}
        cy={center}
        r={center - 0.5}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.3}
        strokeWidth={1}
      />
      <circle
        className="wedge"
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={size / 2}
        strokeDasharray={circumference}
      />
    </Pie>
  )
}

export default CountdownPie
